import { RefreshCw, Sparkles, X } from "lucide-react";
import { usePwaUpdate } from "./UpdateContext";

/**
 * Floating prompt notification displayed when a new frontend build has been deployed and
 * installed in the background by the service worker.
 *
 * Provides Persian RTL text, clear visual cues, and an explicit action to activate the new
 * version and reload the application.
 */
export function PwaUpdatePrompt() {
  const {
    needRefresh,
    isUpdating,
    updateApp,
    dismiss,
    required,
    inFlight,
    frontend,
  } = usePwaUpdate();

  if (!needRefresh || required) {
    return null;
  }

  return (
    <aside
      role="alert"
      aria-live="polite"
      className="fixed inset-x-4 bottom-4 z-50 mx-auto max-w-md sm:inset-x-auto sm:right-6 sm:bottom-6"
    >
      <div className="border-brand/20 relative flex flex-col gap-3 rounded-2xl border bg-white/95 p-4 shadow-2xl backdrop-blur-md transition-all duration-300 dark:border-slate-700 dark:bg-slate-900/95">
        {/* Header with icon and dismiss button */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="bg-brand/10 text-brand flex h-10 w-10 shrink-0 items-center justify-center rounded-xl dark:bg-sky-500/20 dark:text-sky-400">
              <Sparkles className="h-5 w-5 animate-pulse" />
            </div>
            <div>
              <h2 className="text-title-sm font-bold text-slate-900 dark:text-white">
                نسخه جدید در دسترس است
              </h2>
              <p className="text-caption text-slate-600 dark:text-slate-300">
                نسخه جدید سامانه کایر با آخرین تغییرات آماده دریافت است.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={dismiss}
            disabled={isUpdating}
            aria-label="بستن پیام به‌روزرسانی"
            className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {frontend && (
          <details className="text-xs leading-6">
            <summary className="cursor-pointer font-semibold">
              تغییرات نسخه <bdi>{frontend.version}</bdi>
            </summary>
            <ul className="list-inside list-disc">
              {frontend.releases
                .find((release) => release.version === frontend.version)
                ?.notes.filter((note) => note.userFacing)
                .map((note, index) => (
                  <li key={index}>{note.text}</li>
                ))}
            </ul>
          </details>
        )}

        {/* Action buttons */}
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-1 dark:border-slate-800">
          <button
            type="button"
            onClick={dismiss}
            disabled={isUpdating}
            className="text-caption rounded-lg px-3 py-1.5 font-medium text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
          >
            بعداً
          </button>
          <button
            type="button"
            onClick={() => void updateApp()}
            disabled={isUpdating || inFlight > 0}
            className="bg-brand text-caption hover:bg-brand-hover active:bg-brand-dark inline-flex items-center gap-2 rounded-xl px-4 py-2 font-semibold text-white shadow-sm transition-all disabled:pointer-events-none disabled:opacity-60"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${isUpdating ? "animate-spin" : ""}`}
            />
            <span>
              {isUpdating ? "در حال به‌روزرسانی…" : "به‌روزرسانی اکنون"}
            </span>
          </button>
        </div>
      </div>
    </aside>
  );
}
